import {css, html, LitElement, unsafeCSS} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {getData} from "../services/cacheService.ts";
import {createCalendar, Interaction, List, ResourceTimeGrid, ResourceTimeline} from '@event-calendar/core';
import eventCalendarStyles from '@event-calendar/core/index.css?raw' with {type: 'css'};
import {
  calculateTimes,
  filterEventsByRessource,
  filterEventsByTimeRange,
  transformItemsToEvents,
  findCurrentDayInDayRange,
  transformFilterToStages,
  transformFilterToDays
} from "../utils/transformer.ts";

@customElement('artist-timeline')
export class ArtistTimeline extends LitElement {

  @property()
  api:string = '/timetable-api?timetable-festival=1';

  @state()
  private loading: boolean = true
  private noEvents: boolean = false
  private container!: HTMLElement;
  private eventCalendar:any = null;
  private events?: any = []
  private filteredEvents?: any = []
  private stages?: any = []
  private selectedStages?: any = []
  private days?: any = []
  private currentDate: any = null
  private view: string = 'resourceTimelineWeek'
  private stagesLabel: string = 'Stages'

  /*
  createRenderRoot() {
    return this;
  }
*/
  connectedCallback() {
    super.connectedCallback()
  }

  updateEventCalendar () {
    if (!this.eventCalendar) {return}

    // update Stages
    this.eventCalendar.setOption('resources', this.selectedStages)

    // update Events
    this.filteredEvents = filterEventsByRessource(this.events, this.selectedStages)
    this.eventCalendar.setOption('events', this.filteredEvents)

    // check for Day
    let currDay = findCurrentDayInDayRange(this.days)
    if (currDay) {
      // show current day, because it is in range
      this.currentDate = currDay
      this.eventCalendar.setOption('nowIndicator', true)
    } else if (!this.currentDate){
      // show second day, as default, else first
      let dayIndex = this.days[2] ? 2 : 0
      this.currentDate = this.days[dayIndex]
    }

    // Filter Events by Date
    this.filteredEvents = filterEventsByTimeRange(this.filteredEvents, this.currentDate)
    const times = calculateTimes(this.filteredEvents);
    this.eventCalendar.setOption('events', this.filteredEvents)
    this.eventCalendar.setOption('duration', { days: 1 })
    this.eventCalendar.setOption('date', this.currentDate.min)

    // disable stages that has no events
    /*
    let tempfilterEvents = filterEventsByTimeRange(this.events, this.currentDate)
    let tempstages = filterStagesByEvents(tempfilterEvents, this.stages)
    this.stages = tempstages
    console.log('tempfilterEvents',tempfilterEvents)
    console.log('tempstages',tempstages)
    */

    if (this.filteredEvents.length === 0) {
      this.noEvents = true
      this.eventCalendar.setOption('slotMinTime', {minutes: 0})
      this.eventCalendar.setOption('slotMaxTime', {minutes: 0})
    } else {
      this.noEvents = false
      this.eventCalendar.setOption('slotMinTime', {minutes: times.earliestStartInMinutes})
      this.eventCalendar.setOption('slotMaxTime', {minutes: times.latestEndInMinutes})
      this.eventCalendar.setOption('scrollTime', {minutes: 0} )
    }

    // scroll to Offset in minutes before event start on mainstage
    let mobile = window.innerWidth <= 1024
    let offsetInMinutes = 30
    let mainstageEvents = this.filteredEvents.filter((event:any) => event.resourceId ==='stage-1')
    if (mobile && mainstageEvents.length > 0) {
      let mainstageEventsTimes = calculateTimes(mainstageEvents);
      this.eventCalendar.setOption('scrollTime', {minutes: mainstageEventsTimes.earliestStartInMinutes - offsetInMinutes} )
    }

    this.requestUpdate()
  }

  firstUpdated() {
    this.container = this.renderRoot.querySelector('#timeline') as HTMLElement;
    this.initializeTimeline();
    this._getData()
  }

  private initializeTimeline() {

    this.eventCalendar = createCalendar(
      // HTML element the calendar will be mounted to
      this.container,
      // Array of plugins
      [
        List,
        ResourceTimeline,
        ResourceTimeGrid,
        Interaction
      ],
      // Options object
      {
        view: 'resourceTimelineDay',
        headerToolbar: {
          start: '',
          center: '',
          end: ''
        },
        locale: document.documentElement.lang,
        titleFormat: {weekday:'long' ,year: 'numeric', month: 'long', day: 'numeric'},
        resources: this.stages,
        events: this.events,
        allDaySlot: false,
        displayEventEnd: true,
        nowIndicator: false,
        pointer: false,
        firstDay: 1, // Wochenstart mit Montag
        flexibleSlotTimeLimits: true,
        filterResourcesWithEvents: true, // Blendet leere Ressourcen (Stages) aus
        eventStartEditable: false,
        eventDurationEditable: false,
        slotEventOverlap: false,
        // eventClick: this.itemClick,
        slotWidth: 144,
        eventContent: this.eventContentRender
      }
    );
  }

  private eventContentRender(info:any) {
    const date1 = new Date(info.event.start);
    const hours1 = date1.getHours().toString().padStart(2, '0');
    const minutes1 = date1.getMinutes().toString().padStart(2, '0');
    const time1 = `${hours1}:${minutes1}`;
    const date2 = new Date(info.event.end);
    const hours2 = date2.getHours().toString().padStart(2, '0');
    const minutes2 = date2.getMinutes().toString().padStart(2, '0');
    const time2 = `${hours2}:${minutes2}`;
    const timeText = `${time1} - ${time2}`

    let html = `
        <time class="ec-event-time">${timeText} </time>
        <h4 class="ec-event-title">${info.event.title}</h4>
        <span class="ec-event-stage">${info.event.extendedProps.stage}</span>
    `
    return { html: html }
}

  private async _getData() {
    let url = this.api
    try {
      const data = await getData(url);
      if (data) {
        this.stages = transformFilterToStages(data.filter.stages)
        this.selectedStages = this.stages
        this.events = transformItemsToEvents(data.items, this.stages)
        this.days = transformFilterToDays(data.filter.days)
        this.updateEventCalendar()
      }
    } catch (error) {
      console.error({ error });
    } finally {
      this.loading = false
      this.requestUpdate()
    }
  }

  private _filterDay(day:any) {
    this.eventCalendar.setOption('date', day.min)
    this.currentDate = day
    this.updateEventCalendar()
  }
  private _changeView(view:any) {
    this.eventCalendar.setOption('view', view)
    this.view = view
    this.requestUpdate()
  }

  private _handleCheckboxChange() {
    // Alle Checkboxen abfragen
    const checkboxes = this.renderRoot.querySelectorAll('.stage-checkbox');
    // IDs der aktivierten Checkboxen sammeln
    const checkedIds = new Set();
    checkboxes.forEach((cb, index) => {
      let cbb = cb as HTMLInputElement
      if (cbb.checked) {
        checkedIds.add(this.stages[index].id);
      }
    });

    // Wenn keine aktiv, zurück zu allen
    if (checkedIds.size === 0) {
      this.selectedStages = [...this.stages];
      this.stagesLabel = 'Stages'
    } else {
      this.selectedStages = this.stages.filter((item:any) => checkedIds.has(item.id));
      this.stagesLabel = this.selectedStages.map((item:any) => item.title).join(', ')
    }
    this.updateEventCalendar()
  }

  private isActiveDay(day:any){
    if(day.id === this.currentDate.id) {
      return 'active'
    }
    return ''
  }

  render() {
      return html`

          ${this.loading
            ? html`<div part="loading-overlay" class="loading-overlay">
                      <div class="loading-spinner">
                          <svg part="pv-loader" class="pv-loader" viewBox="0 0 40 40">
                              <defs>
                                  <style>
                                      #bg {
                                          fill: #fff;
                                      }

                                      #pv {
                                          fill: #1d1d1b;
                                      }
                                      .rotate-bg {
                                          transform-origin: center;
                                          animation: rotate-bg_ani 10s linear infinite
                                      }
                                      @keyframes rotate-bg_ani {
                                          100% {
                                              transform: rotate(360deg)

                                          }
                                      }
                                  </style>
                              </defs>
                              <g>
                                  <g id="pv-signet">
                                      <path id="bg"
                                            class="rotate-bg"
                                            d="M38.9,21.7c-1.1-.9-1.1-2.4,0-3.3,1.1-.9.9-2.1-.4-2.6-1.3-.5-1.8-1.9-1-3.1.8-1.2.2-2.3-1.2-2.4-1.4,0-2.3-1.3-1.9-2.7.3-1.4-.5-2.2-1.9-1.9-1.4.3-2.6-.5-2.7-1.9,0-1.4-1.2-2-2.4-1.2-1.2.8-2.6.3-3.1-1-.5-1.3-1.7-1.5-2.6-.4-.9,1.1-2.4,1.1-3.3,0-.9-1.1-2.1-.9-2.6.4-.5,1.3-1.9,1.8-3.1,1-1.2-.8-2.3-.2-2.4,1.2,0,1.4-1.3,2.3-2.7,1.9-1.4-.3-2.2.5-1.9,1.9.3,1.4-.5,2.6-1.9,2.7-1.4,0-2,1.2-1.2,2.4.8,1.2.3,2.6-1,3.1-1.3.5-1.5,1.7-.4,2.6,1.1.9,1.1,2.4,0,3.3-1.1.9-.9,2.1.4,2.6,1.3.5,1.8,1.9,1,3.1-.8,1.2-.2,2.3,1.2,2.4,1.4,0,2.3,1.3,1.9,2.7-.3,1.4.5,2.2,1.9,1.9,1.4-.3,2.6.5,2.7,1.9,0,1.4,1.2,2,2.4,1.2,1.2-.8,2.6-.3,3.1,1,.5,1.3,1.7,1.5,2.6.4.9-1.1,2.4-1.1,3.3,0,.9,1.1,2.1.9,2.6-.4.5-1.3,1.9-1.8,3.1-1,1.2.8,2.3.2,2.4-1.2,0-1.4,1.3-2.3,2.7-1.9,1.4.3,2.2-.5,1.9-1.9-.3-1.4.5-2.6,1.9-2.7,1.4,0,2-1.2,1.2-2.4-.8-1.2-.3-2.6,1-3.1,1.3-.5,1.5-1.7.4-2.6ZM38.5,17.1s0,0,0,0h0s0,0,0,0c0,0,0,0,0,0,0,0,0,.3-.3.5h0c.2-.3.3-.4.3-.5ZM1.5,17.1h0s0,0,0,0,0,0,0,0c0,0,.2-.2.4-.4h0c-.3.2-.4.3-.4.4,0,0,0,0,0,0ZM2.1,23.4c0,0-.1,0-.2,0-.2,0-.3-.2-.4-.2h0s.2,0,.4.1c0,0,.1,0,.2,0h0ZM1.8,22.5s0,0,0,0h0c0-.1,0-.1,0-.2.1-.1.3-.2.4-.4h0c-.1.2-.2.3-.4.4ZM2.3,18.1c-.1-.2-.3-.3-.4-.4h0c.2,0,.3.2.4.4h0ZM37.4,16.3c.2.1.4.2.5.3h0c-.2,0-.4,0-.6-.2h0ZM37.8,18c-.1.2-.3.3-.4.5h0c.1-.2.2-.4.4-.6h0ZM36.1,25.2c0-.1,0-.2.1-.3h0c0,.1,0,.2-.1.3,0,0,0,.2,0,.3h-.1c0-.1,0-.2,0-.3ZM36,14.3h.1c0,.1,0,.3.1.4,0,0,0,.1,0,.2h0s0,0,0-.1c0-.1,0-.3-.1-.4ZM36.3,27.4c0,.2.2.4.3.5h0c-.1-.1-.2-.3-.3-.5h0ZM37,15.9h0c-.2,0-.3-.2-.4-.4h0c.1.1.2.3.4.4ZM36.6,12.1c0,.1-.2.3-.2.4h0c0-.2.1-.3.2-.5,0,0,0,0,0-.1h0c0,0,0,.1,0,.2ZM36,11.2c0,0,.1,0,.2,0,.2,0,.4,0,.4.1h0c0,0-.2,0-.4,0,0,0-.1,0-.2,0h0ZM36.1,13.1h.1c0,.2,0,.4,0,.6h-.1c0-.2,0-.4,0-.6ZM34.8,10.9c.2,0,.4.2.6.2h0c-.2,0-.4,0-.6-.1h0ZM33.2,8.4h-.1c0,0,0-.1,0-.2,0-.1,0-.3,0-.4h.1c0,.1,0,.3,0,.4s0,.1,0,.2ZM33.9,10.1c.1.2.3.3.4.4h0c-.2,0-.3-.2-.4-.4h0ZM33.5,9.6c0-.2-.2-.4-.2-.6h.1c0,.2.1.4.2.5h0ZM33.1,6.6c0,0,.2,0,.2,0,0,0,0,.2,0,.3s0,.1,0,.2h-.1c0,0,0-.1,0-.2,0-.1,0-.2,0-.2,0,0,0,0-.2,0h0ZM31.8,6.8c.2,0,.4,0,.6,0h0c-.2.1-.4.2-.6.2h0c0-.1,0-.1,0-.1ZM30.6,6.5c.2,0,.4.1.6.2h0c-.2,0-.4,0-.6,0h0ZM29.6,5.8c.1.1.3.3.5.4h0c-.2,0-.3-.2-.5-.3h0ZM29,4.8c0,.2.2.4.3.6h0c-.1-.1-.2-.3-.3-.5h0ZM28.7,3.6c0,0,0,.2,0,.2,0,.1,0,.3,0,.4h-.1c0-.1,0-.2,0-.4,0,0,0-.2,0-.2h.1ZM27.9,3.4c.1,0,.2-.1.3-.1h0c0,.1-.2.2-.3.2,0,0-.2.1-.3.2h0c0-.1.2-.2.3-.2ZM27.1,3.8h0c-.2.2-.4.2-.6.2h0c.2-.1.4-.2.6-.2ZM25.3,3.8c.2,0,.4.1.6.1h0c-.2,0-.4,0-.6,0h0ZM24.2,3.2c.2.1.3.3.5.4h0c-.2,0-.4-.1-.5-.3h0ZM23.5,2.2c0,.2.2.4.3.5h0c-.1-.1-.2-.3-.3-.5h0ZM22.9,1.4c0,0,.2,0,.3.2h0c0,0-.2-.1-.2-.1,0,0-.1,0-.2.1h0c.1-.2.2-.2.3-.2ZM22.1,2.1h0c-.2.2-.3.4-.5.5h0c.2-.2.3-.3.5-.5ZM21.1,2.7h0c-.2.2-.4.2-.6.3h0c.2-.1.4-.2.6-.3ZM19.3,2.8c.2,0,.4,0,.6,0h0c-.2,0-.4,0-.6,0h0ZM18.2,2.3c.2.1.3.2.5.3h0c-.2,0-.4-.1-.5-.2h0ZM17.2,1.5c0,0,.2.1.4.3,0,0,0,0,.1.1h0s0,0-.1,0c-.1-.2-.2-.2-.3-.3h0ZM16.7,1.9s0,0,0-.1h0c0,0,0,.1,0,.2,0,.2-.1.3-.2.5h0c0-.2.2-.3.2-.5ZM16.1,2.9h0c-.1.2-.3.4-.5.5h0c.2-.2.3-.4.4-.5ZM15.1,3.6h0c-.2.2-.4.3-.6.3h0c.2-.2.4-.2.6-.3ZM13.3,3.9c.2,0,.4,0,.6,0h0c0,.1,0,.1-.1.1-.2,0-.3,0-.5,0h0ZM12.2,3.5c.2.1.4.2.6.3h0c-.2,0-.4,0-.6-.2h0ZM11.4,3.3s0,0,.2,0,0,0,0,0h0c0,.1,0,.1,0,.1,0,0,0,0,0,0,0,0-.1.1-.1.4h-.1c0-.3.1-.5.2-.5ZM11.2,4.4h.1c0,.2-.1.4-.2.6h0c0-.2.1-.4.2-.6ZM10.7,5.5h0c-.1.2-.3.4-.4.5h0c.1-.2.3-.4.4-.5ZM9.8,6.3h0c-.2.2-.4.3-.6.4h0c.2-.2.4-.3.6-.4ZM8,6.8c.2,0,.4,0,.6,0h0c-.2.1-.3.1-.5.1s-.1,0-.2,0h0ZM7.3,6.7s0,0,0,0h0c0,0,0,0,0,0-.1,0-.2,0-.3,0s-.2,0-.2,0h0c.1-.2.3-.2.6,0ZM6.8,7.3h0c0,.2,0,.4,0,.6h-.1c0-.2,0-.4,0-.6h0ZM6.7,8.5h.1c0,.2,0,.4-.1.6h0c0-.2.1-.4.1-.6ZM6.2,10c0,0,.1-.2.2-.3h0c0,.1-.1.2-.2.3,0,0-.1.2-.2.2h0c0-.1.1-.2.2-.3ZM5.6,10.6h0c-.2.2-.4.3-.5.4h0c.2-.2.4-.3.5-.4ZM4.4,11.1h0c-.2.2-.4.2-.6.2h0c.2-.1.4-.2.6-.2ZM3.8,12.6c0,.2.1.4.2.6h-.1c0-.2,0-.4-.2-.6h0ZM4,13.9c0,.2,0,.4,0,.6h-.1c0-.2,0-.4,0-.6h.1ZM3.7,15.1c0,.2-.2.4-.3.5h0c.1-.2.2-.4.3-.6h0ZM3.8,25.1h0c0-.2-.2-.3-.3-.5h0c.1.1.2.3.3.5ZM3.2,11.5h.1s0,0,0,0c0,0,0,.2.2.5h0s0,0,0,0h0c-.1-.2-.2-.4-.2-.6s0,0,0,0ZM3,20c0,.1,0,.3,0,.4h-.1c0-.2,0-.3,0-.4s0-.1,0-.2h.1c0,0,0,.1,0,.2ZM2.8,19.2c0-.2-.1-.4-.2-.6h0c0,.1.2.3.2.5h-.1ZM2.4,16.4c.2-.1.3-.2.5-.4h0c-.2.2-.3.3-.5.5h0ZM2.5,21.6c.1-.2.2-.4.3-.6h0c0,.2-.2.4-.3.6h0ZM3.1,24.1c-.1-.1-.3-.3-.5-.4h0c.2,0,.3.2.5.3h0ZM3.4,28.7c0,0-.2,0-.2-.1,0,0,0,0,0,0,0,0,0-.2.1-.4h0c0,.2,0,.3,0,.4s0,0,0,0c0,0,0,0,.1,0h0ZM3.7,27.6h0c0-.2.2-.4.2-.6h.1c0,.2-.1.4-.2.6ZM4,26.4h-.1c0,0,0-.1,0-.2,0-.2,0-.3,0-.5h.1c0,.1,0,.3,0,.5,0,0,0,.1,0,.2ZM4.6,28.9c-.2,0-.4-.1-.6-.1h0c.2,0,.4,0,.6,0h0ZM5.7,29.5c-.2-.1-.3-.2-.5-.4h0c.2,0,.4.1.5.3h0ZM6.5,30.5c0-.2-.2-.3-.3-.5,0,0,0,0,0,0h0s0,0,0,0c.1.1.2.3.3.5h0ZM6.8,32.7c0,0,0,.2,0,.2h-.1c0,0,0-.2,0-.2,0-.1,0-.3,0-.4h.1c0,.1,0,.3,0,.4ZM6.8,31.7c0-.2,0-.4,0-.6h.1c0,.2,0,.4,0,.6h-.1ZM7.3,33.3c-.1,0-.2,0-.3,0h0c0-.1.2-.1.3-.1,0,0,.2,0,.3,0h0c0,.1-.2.1-.3.2ZM8.8,33.3c-.2,0-.4,0-.6,0h0c.2-.1.4,0,.6,0h0ZM10,33.8c-.2-.1-.3-.2-.5-.3h0c.2,0,.4,0,.6.2h0ZM10.8,34.7c-.1-.2-.2-.3-.4-.5h0c.1,0,.3.3.4.4h0ZM11,35.2h0c0,.2.1.4.2.6h-.1c0-.2,0-.4-.2-.6ZM11.6,36.8c0,0-.1,0-.2,0,0,0,0-.1-.1-.3h.1c0,.1,0,.2,0,.2,0,0,0,0,0,0s.1,0,.2,0h0c-.1.1-.2.2-.3.2ZM12.4,36.4h0c.2-.2.4-.3.6-.3h0c-.2.2-.4.2-.6.3ZM14.2,36.1c-.2,0-.4,0-.6,0h0c.2-.1.4-.1.6,0h0ZM15.3,36.5c-.2-.1-.4-.2-.6-.3h0c.2,0,.4,0,.6.2h0ZM16.2,37.3c-.1-.2-.3-.3-.4-.5h0c.2,0,.3.2.4.4h0ZM16.8,38.4c0,0-.1-.2-.2-.3,0,0,0-.2-.1-.3h0s0,.1.1.2c0,.1,0,.2.2.3h0ZM17.6,38.2c0,0-.1.1-.2.2h0c0-.1.1-.2.2-.2,0-.1.2-.2.3-.3h0c0,.2-.2.3-.3.4ZM18.3,37.6h0c.2-.2.4-.3.6-.4h0c-.2.2-.4.3-.5.4ZM20.1,37.1c-.2,0-.4,0-.6,0h0c.2-.1.4-.2.6-.2h0ZM21.3,37.4c-.2,0-.4-.2-.6-.2h0c.2,0,.4,0,.6.1h0ZM22.3,38.1c-.1-.2-.3-.3-.5-.4h0c.2,0,.3.2.5.3h0ZM22.9,38.6c0,0-.1,0-.2,0h0c0,0,.1,0,.1,0,0,0,.1,0,.3-.3h0c-.1.3-.2.4-.3.4ZM23.5,37.7h0c.1-.2.2-.4.4-.6h0c-.1.2-.2.4-.3.6ZM24.3,36.7h0c.2-.2.3-.3.5-.4h0c-.2.2-.4.3-.5.4ZM25.4,36.2h0c.2-.2.4-.2.6-.2h0c-.2.1-.4.1-.6.2ZM27.2,36.3c-.2,0-.4-.1-.6-.1h0c.2,0,.4,0,.6,0h0ZM28.4,36.8c0,0-.2,0-.4-.2,0,0,0,0-.1,0h0c0,0,0,0,.1,0,.2.1.3.2.4.2h0ZM28.8,36.2s0,0,0,.1h-.1s0,0,0-.1c0-.2,0-.4,0-.5h.1c0,.2,0,.4,0,.5ZM29,35.1h0c0-.2.2-.4.3-.6h0c-.1.2-.2.4-.3.6ZM29.7,34.1h0c.2-.2.3-.4.5-.5h0c-.2.2-.3.3-.5.5ZM30.7,33.4h0c.2-.2.4-.2.6-.3h0c-.2.1-.4.2-.6.3ZM32.6,33.3c-.2,0-.4,0-.6,0h0c.2,0,.4,0,.6,0h0ZM33.1,31.8c0-.1,0-.2,0-.3h.1c0,.1,0,.2,0,.3,0,0,0,.2,0,.3h-.1c0,0,0-.2,0-.3ZM33.3,33.3s0,0-.1,0h0c0-.1,0-.1,0-.1,0,0,0,0,0-.2,0,0,0-.2,0-.3h.1c0,0,0,.2,0,.3,0,.1,0,.2,0,.3ZM33.4,30.9h-.1c0-.2.1-.4.2-.6h0c0,.2-.2.4-.2.6ZM34,29.8h0c.1-.2.3-.4.5-.5h0c-.2.2-.3.3-.4.5ZM34.9,29.1h0c.2-.2.4-.3.6-.3h0c-.2.2-.4.2-.6.3ZM36,26.2h0s.1,0,.1,0h0c0,.2,0,.4,0,.6h-.1c0-.2,0-.4,0-.6ZM36.2,28.8h0s0-.1,0-.1h0c.3,0,.5-.1.5-.2h0c0,.1-.3.2-.6.3ZM36.6,24.4h0c.1-.2.3-.4.4-.5h0c-.2.2-.3.4-.4.5ZM37.1,20.9c0-.2-.1-.4-.1-.6h.1c0,.2,0,.4.1.6h-.1ZM37.1,19.6h-.1c0-.2,0-.4.1-.6h0c0,.2-.1.4-.1.6ZM37.4,21.4c0,.2.2.4.3.5h0c-.1-.1-.3-.3-.4-.5h0ZM37.5,23.6h0c.2-.2.4-.3.6-.4h0s0,0,0,0c-.2,0-.4.2-.5.3ZM38.5,22.9c0,0-.1-.2-.3-.4,0,0,0,0,0,0h0c0,0,0,0,0,0,.3.2.3.4.3.5h-.1Z"/>
                                      <path id="pv"
                                            class="scale-pv"
                                            d="M32,12s0,0,0,0c0,0,0,0,0,0,0,0,0,0,0,0-.1,0-.3,0-.4-.2-.1,0-.2,0-.3,0,0,0,0,0,0,0,0,0-.2,0-.2,0-.3,0-.6.1-.8.3,0,0,0,0,0,.1-.1,0-.1.1-.3.1,0,0,0,.1-.2.2,0,0,0,0,0,.1,0,0,0,0-.2.1,0,.1-.2.2-.3.3-.5,1.2-.9,2.4-1.4,3.6-.5,1.3-.9,2.5-1.4,3.8-.5,1.3-.9,2.5-1.4,3.8-.2.6-.4,1.2-.7,1.8,0,.2-.3.2-.4.4-.1-.2-.3-.2-.3-.5,0,0-.1-.2-.2-.3-.2-.4-.4-.8-.6-1.2-.1-.4-.4-.7-.6-1.1-.1-.3.4-.8.6-1.1.2-.2.7-.9.6-1.2.4-.8.4-1.5.4-2.4,0-.6-.2-1-.3-1.6,0-.3-.3-.8-.2-1.2,0-.2-.1-.3-.3-.4h0c0-.2-.1-.2-.2-.3,0,0,0,0,0-.1-.1-.2-.3-.4-.3-.6,0,0-.1-.1-.1-.2-.2-.1-.2-.4-.3-.6-.1-.3-.3-.5-.4-.8-.1-.2-.8-.9-.8-.8-.6-.4-1.1-1-1.8-1.2-.2,0-.3,0-.5-.1,0,0-.2,0-.2-.1-.7-.1-1.5,0-2.1,0-.3,0-.6.1-.9.1-.2,0-.5,0-.7.1-.6.2-1.1.4-1.6.7-.3-.2-.6-.4-.9-.6,0,0,0,0,0-.1-.1,0-.3-.1-.4-.1,0,0,0,0,0,0,0,0,0,0,0,0-.6-.1-1.1-.2-1.6-.3-.6,0-1,.4-1.5.4,0,0-.2.1-.3.1-.2.2-.6.3-.6.7,0,.3.4.5.7.5.4,0,.7-.2,1.1-.2.2,0,.5,0,.7,0,.2.1.4.2.6.2.1.1.3.1.4.3.1,0,.2,0,.3.2,0,.2-.5.5-.7.7-.2.3-.4.7-.7.9,0,.8-.7,1.5-.4,2.3.1.2.2.4.3.6,0,.2.3.4.3.6,0,0,.1,0,.2.1.7.2,1.3.8,2.1.7.4,0,1.6-.5,1.6-1,.2-.1.2-.4.4-.5,0-.4.6-.8.5-1.2,0,0,0,0,0-.1,0-.2,0-.3,0-.4-.2-.2-.1-.5-.1-.7,0-.3,0-.7-.2-.9,0-.1-.6-.7-.4-.8.2-.1.5-.2.7-.2.7,0,1.4.2,2,.5.5.2,1.2.6,1.6,1.1,0,0,.1,0,.2,0,0,.1.2.2.3.3.1.2.4.4.5.6.2.2.3.5.5.8,0,0,0,0,0,.1,0,0,0,.2.2.2,0,0,0,0,0,.1.1.1.2.3.3.5,0,0,0,0,0,0,0,0,0,.2.1.3,0,0,0,0,0,.1,0,0,0,0,0,0,0,.1,0,.2,0,.2,0,0,0,0,.1.2,0,.4.2,1,.3,1.4,0,.5,0,1.2-.2,1.7-.1-.2-.2-.5-.3-.8,0,0,0,0,0,0,0-.2-.4-.8-.5-1-.3-.5-.6-1-.9-1.5-.3-.4-.6-.8-.9-1.2-.2-.3-.4-.1-.7-.1-.1,0-.3.2-.5.2,0,0-.1.1-.2.2,0,0-.2,0-.2.2-.1.1-.1.3-.2.4,0,.2.1.3.1.5.2.1.1.4.3.5,0,0,0,0,0,.1.3.5.4,1.1.6,1.7.2,0,.3.6.4.8.3.3.3.7.5.9,0,.5.5,1,.7,1.4.1.2.5.6.3.8-.2.2-.4.3-.6.5-.5.3-1.1.2-1.7,0-.3,0-.6,0-.9-.3-.2-.1-.4-.4-.6-.5-.1-.4-.5-.8-.7-1.2-.2-.4-.3-.8-.4-1.3,0-.3,0-.7-.4-.6-.2,0-.5.2-.6.2,0,0-.2,0-.4,0,0,0-.1,0-.2,0,0,.1-.2.1-.3.2-.7-.1-1.3.9-1.4,1.4,0,.3,0,.8,0,1.1,0,.4,0,.9,0,1.4,0,1,.1,2,0,3,0,.8-.2,2.1.7,2.4,0,0,0,0,0,0,0,0,.2,0,.4,0,.3.1.6,0,.9-.1.1-.2.4-.5.6-.6.3-.2.4-.5.5-.8,0-.3,0-.7.1-1,0-.3.2-.7,0-1,0-.4,0-.8,0-1.2,1.1.7,2.5.7,3.7.4,0,0,0,0,0,0,.1,0,.3-.1.3-.2.2,0,.4-.1.6-.1,0,0,0,0,0,0,0,0,0,0,0-.1.2,0,.4-.2.6-.2.1.1.1.3.3.4,0,0,0,0,0,.1,0,0,0,.1.2.2,0,.1,0,0,0,.2,0,0,0,.1,0,.1.1.1.2.3.3.5.3.3.4.8.7,1.2,0,.2.2.4.3.6.1.3.2.6.4.8,0,.2.2.4.2.6.2.1.4.2.7.3.4,0,.8,0,1.1-.1.2-.1.5-.4.6-.6.1-.4.5-.6.7-1,.2-.4.4-.9.6-1.3.9-1.9,1.8-3.7,2.5-5.6.6-1.5,1.1-3,1.6-4.4.1-.3.3-.7.4-1,0-.1.2-.3.3-.4,0-.3.1-.6.2-.8.1-.5.3-.9.4-1.4,0-.2,0-.4,0-.6,0,0,0-.3,0-.5,0,0,0,0,0,0ZM13.2,15.6c-.1.3-.2.9,0,1.1-.3,0-.6,0-.9-.1,0-.1-.2-.2-.3-.3,0-.3,0-.9,0-1.2,0-.6.4-1,.7-1.3,0,0,0,0,0,0,.1,0,.3.9.4,1,0,.2,0,.5,0,.7,0,0,0,.1,0,.2Z"/>
                                  </g>
                              </g>
                          </svg>
                          <svg part="sh-loader" class="sh-loader" viewBox="0 0 40 40">
                              <defs>
                                  <style>
                                      #sh-signet {
                                          transform-origin: center;
                                          animation: rotate-bg_ani_sh 3s linear infinite
                                      }
                                      @keyframes rotate-bg_ani_sh {
                                          0% { transform: rotate(0deg) }
                                          25% { transform: rotate(180deg) }
                                          50% { transform: rotate(180deg)}
                                          75% { transform: rotate(360deg) }
                                          100% { transform: rotate(360deg) }
                                      }
                                  </style>
                              </defs>
                              <g id="sh-signet">
                                  <path d="M25.5,3.3v14.8s-11,0-11,0V7.6s3.6-2.2,3.6-2.2v9.1s3.7,0,3.7,0V1.1S19.9,0,19.9,0L3.5,10v20s10.9,6.7,10.9,6.7v-14.8s11,0,11,0v10.5s-3.6,2.2-3.6,2.2v-9.1s-3.7,0-3.7,0v13.4s1.9,1.1,1.9,1.1l16.5-10V10s-10.9-6.7-10.9-6.7h0ZM10.8,30.2l-3.7-2.2v-15.9s3.6-2.2,3.6-2.2v20.4h0,.1ZM32.8,28l-3.6,2.2V9.8s3.6,2.2,3.6,2.2v15.9s0,.1,0,.1Z"/>
                              </g>
                          </svg>
                      </div>
                  </div>`
            : null
          }

          <div class="ce-filters">
              <div class="ce-day-filters">
                  ${this.days.map((day: any) => html`
                    <button class="ec-day-filer ${this.isActiveDay(day)}" @click="${() => this._filterDay(day)}">
                        <span class="daymonth">${day.dayMonth}</span>
                        <span class="weekday">${day.weekday}</span>
                    </button>
                `)}
              </div>

              <z3-dropdown class="ce-stage-filter" closeonclickinside label="${this.stagesLabel}" autowidth="false">
                  ${this.stages.map((stage:any) => html`
                    <label class="checkbox ${stage.enabled ? '' : 'disabled'}">
                        <input class="stage-checkbox"
                               type="checkbox"
                               .disabled="${!stage.enabled}"
                               @change="${this._handleCheckboxChange}">
                        <span>${stage.title}</span>
                    </label>
                `)}
              </z3-dropdown>

              <div class="view-buttons">
                  <button class=${this.view === 'resourceTimelineDay' || this.view === 'resourceTimelineWeek' ? 'active' : ''}
                          @click="${() => this._changeView('resourceTimelineDay')}">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 19">
                          <path d="M3.94,19h12.14c.57,0,.93-.38.93-.97v-2.05c0-.6-.35-.98-.93-.98H3.94c-.59,0-.94.37-.94.98v2.05c0,.59.34.97.94.97Z"/>
                          <path d="M5.98,14h5.06c.61,0,.96-.37.96-.97v-2.06c0-.61-.36-.97-.96-.97h-5.06c-.62,0-.98.37-.98.97v2.06c0,.61.36.97.98.97Z"/>
                          <path d="M13.98,14h5.06c.61,0,.96-.37.96-.97v-2.06c0-.61-.36-.97-.96-.97h-5.06c-.62,0-.98.37-.98.97v2.06c0,.61.36.97.98.97Z"/>
                          <path d="M12.93,4h6.15c.58,0,.92-.37.92-.97V.97C20,.37,19.66,0,19.08,0h-6.15C12.34,0,12,.37,12,.97v2.06c0,.61.34.97.93.97Z"/>
                          <path d="M3.89,9h13.24c.54,0,.87-.38.87-.98v-2.05c0-.59-.33-.97-.87-.97H3.89c-.56,0-.89.37-.89.97v2.05c0,.6.33.98.89.98Z"/>
                          <path d="M.94,4h9.14c.56,0,.92-.38.92-.97V.96c0-.6-.35-.96-.92-.96H.94C.34,0,0,.37,0,.96v2.07c0,.6.34.97.94.97Z"/>
                      </svg>
                  </button>
                      <!--
              <button class=${this.view === 'resourceTimeGridDay' ? 'active' : ''}
                      @click="${() => this._changeView('resourceTimeGridDay')}">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 19">
                      <path d="M.83,19h7.35c.5,0,.82-.38.82-.99V.98c0-.6-.32-.98-.82-.98H.83C.31,0,0,.37,0,.98v17.04c0,.6.31.99.83.99Z"/>
                      <path d="M11.83,19h7.35c.5,0,.82-.38.82-.99V.98c0-.6-.32-.98-.82-.98h-7.35c-.53,0-.83.37-.83.98v17.04c0,.6.31.99.83.99Z"/>
                  </svg>
              </button>
              -->
                  <button class=${this.view === 'listWeek' ? 'active' : ''}
                          @click="${() => this._changeView('listWeek')}">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 19">
                          <path d="M.87,9h18.28c.53,0,.85-.38.85-.98v-2.05c0-.59-.32-.97-.85-.97H.87c-.55,0-.87.37-.87.97v2.05c0,.6.32.98.87.98Z"/>
                          <path d="M.87,4h18.28c.52,0,.85-.38.85-.97V.96c0-.6-.33-.96-.85-.96H.87C.32,0,0,.37,0,.96v2.07c0,.6.32.97.87.97Z"/>
                          <path d="M.87,14h18.26c.54,0,.86-.37.86-.97v-2.06c0-.61-.32-.97-.86-.97H.87c-.55,0-.87.37-.87.97v2.06c0,.61.32.97.87.97Z"/>
                          <path d="M.87,19h18.28c.53,0,.85-.38.85-.98v-2.05c0-.59-.32-.97-.85-.97H.87c-.55,0-.87.37-.87.97v2.05c0,.6.32.98.87.98Z"/>
                      </svg>
                  </button>
              </div>
          </div>

          ${this.noEvents
              ? html`
                <div id="timeline-noevents" class="ec">
                </div>`
              : null
          }

          <div id="timeline" class="${this.noEvents ? 'no-events': ''}"></div>
      `
  }

  static styles = [
      unsafeCSS(eventCalendarStyles),
      css`
        :host {
            position: relative;
            display: block;
            overflow: hidden;
            max-width: 100%;
            scroll-margin-top: 5rem;
            --stage-color: var(--color-accent);
            --time-color: var(--color-primary);
            --time-bg: var(--color-purple);
            --timetable-border-color: var(--color-gray);
            --timetable-event-bg-color: var(--color-gray);
            --timetable-list-day-bg-color: var(--color-gray);
            --timetable-list-day-color: inherit;
                        
            --timetable-button-color: var(--color-white);
            --timetable-button-bg: var(--color-gray);
            --timetable-button-active-color: var(--color-primary);
            --timetable-button-active-bg: var(--color-gray);
        }

          .loading-overlay {
              position: absolute;
              z-index: 30;
              inset: 0;
              background: var(--body-bg-color, black);
              opacity: 1;
          }

          .loading-spinner {
              margin: 0 auto;
              width: 3rem;
              height: 3rem;
          }

        #timeline {
            margin-bottom: 2rem;
            transition: 0.3s ease all;
        }
          #timeline.no-events {
              opacity: 0;
          }

          .ec {
              --ec-border-color: var(--timetable-border-color);
              --ec-event-bg-color: var(--timetable-event-bg-color);
              --ec-list-day-bg-color: var(--timetable-list-day-bg-color);
          }

          .ec-timeline .ec-time {
              text-align: left;
              padding-left: 0.5rem;
              color: var(--time-color);
          }

          .ec-timeline .ec-time,
          .ec-timeline .ec-line {
              width: 144px;
          }

          .ec-event {
              border-left: 4px solid #666;
          }
          .ec-event:hover,
          .ec-event:focus {
              border-left: 4px solid var(--color-primary);
          }

          .ec-day-grid .ec-event-body,
          .ec-all-day .ec-event-body,
          .ec-timeline .ec-event-body {
              flex-direction: column-reverse;
              padding: 0.25rem 0.5rem;
              margin-bottom: 2px;
          }
          
          .ec-list .ec-event-body {
              color: var(--timetable-list-day-color);
          }
          

          .ec-event:hover,
          .ec-event:focus {
              min-width: min-content;
              z-index: 10;
              color: black;
              background: var(--color-primary);
          }

          .ec-event-title {
              font-weight: 900;
              font-size: 1rem;
              margin-right: 3px;
          }
          .ec-event-title a {
              color: inherit;
              text-decoration: none;
          }
          .ec-event-title a:hover {
              text-decoration: underline;
          }

          /*
          .ec-event-title-divider {
              color: var(--color-primary);
          }
          .ec-event:hover .ec-event-title-divider,
          .ec-event:focus .ec-event-title-divider {
              color: white;
          }
          */
          .ec-time-grid .ec-event:hover {
              height: unset !important;
              z-index: 2 !important;
          }

          .ec-day-grid .ec-event-time,
          .ec-timeline .ec-event-time {
              font-size: 0.75rem;
          }

          .ec-time-grid .ec-event-stage,
          .ec-timeline .ec-event-stage {
              display: none;
          }

          .uppercase {
              text-transform: uppercase;
          }

          /**/
          .ec-timeline .ec-day-head {
              display: none;
          }

          .ec-timeline .ec-sidebar {
              width: 0;
              border-left: 0;
          }
          .ec-timeline .ec-sidebar .ec-content {
              width: max-content;
              z-index: 2;
              pointer-events: none;
          }
          .ec-timeline .ec-sidebar .ec-resource,
          .ec-timeline .ec-body .ec-days {
              margin-top: 2.5rem;
          }
          .ec-timeline .ec-sidebar .ec-resource {
              margin-top: 0;
              margin-bottom: 2.5rem;
              color: var(--stage-color);
              font-weight: 900;
              border: none !important;
          }

          .view-buttons {
              display: flex;
              gap: calc(var(--spacing)*1);
          }

          .view-buttons button {
              border:none;
              background-color: var(--timetable-button-bg);
              fill: var(--color-white);
              color: var(--color-white);
              line-height: 1;
              padding-inline: calc(var(--spacing) * 4);
              padding-block: calc(var(--spacing) * 3);
          }
          .view-buttons button:first-child  {
              border-top-left-radius: 9999px;
              border-bottom-left-radius: 9999px;
              padding-left: calc(var(--spacing) * 5);
          }
          .view-buttons button:last-child  {
              border-top-right-radius: 9999px;
              border-bottom-right-radius: 9999px;
              padding-right: calc(var(--spacing) * 5);
          }

          .view-buttons button svg {
              display: block;
              width: 1rem;
              height: 1rem;
              fill: currentColor;
          }
          .view-buttons button.active {
              color: var(--timetable-button-active-color);
          }



          .dropdown__trigger {
              font-family: inherit;
              display: inline-flex;
              border-radius: calc(infinity * 1px) !important;
              background-color: var(--timetable-button-bg);
              padding-inline: calc(var(--spacing) * 4);
              padding-block: calc(var(--spacing) * 3);
              font-size: var(--text-sm);
              line-height: var(--tw-leading, var(--text-sm--line-height));
              color: var(--timetable-button-color);
          }

          .dropdown__dropdown {
              margin-top: calc(var(--spacing) * 1);
              border-radius: 1.25rem !important;
              background-color: var(--timetable-button-bg);
              padding: calc(var(--spacing) * 4);
              min-width: auto;
          }

          @media (width >= 64rem) {
              .dropdown__trigger {
                  padding-inline: calc(var(--spacing) * 8);
              }
              .dropdown__dropdown {
                  padding-inline: calc(var(--spacing) * 8);
              }
          }

          .checkbox {
              display: flex;
              position: relative;
              cursor: pointer;
              padding-left: 2rem;
          }
          .checkbox + .checkbox {
              margin-top: 1rem;
          }
          .checkbox [type=checkbox] {
              position: absolute;
              left: 0;
              opacity: 0;
              pointer-events: none;
              padding: 0;
          }
          .checkbox [type=checkbox]+span {
              font-size: 1.25rem;
              text-transform: uppercase;
              margin-top: -2px;
          }
          .checkbox [type=checkbox]+span:before {
              content: "";
              position: absolute;
              top: 1px;
              left: 0;
              width: 20px;
              height: 20px;
              z-index: 0;
              border: 1px solid;
              border-radius: 2px;
              box-shadow: none;
          }
          .checkbox [type=checkbox]+span:after {
              content: "";
              position: absolute;
              top: -3px;
              left: 4px;
              opacity: 0;
              width: 1.5rem;
              height: 1.5rem;
              background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='58.594' height='44.688'%3E%3Cpath d='M18.594 35.313 53.906 0l4.688 4.688-40 40L0 26.093l4.688-4.688Zm50 19.453' style='fill:%2335CC9A'/%3E%3C/svg%3E");
              background-size: contain;
              background-position: 50%;
              background-repeat: no-repeat;
              --tw-text-opacity: 1;
              color: var(--color-accent);
          }
          .checkbox [type=checkbox]:checked+span:after {
              opacity: 1;
          }

          /* disabled Checkbox */
          .checkbox.disabled {
              cursor: not-allowed;
              opacity: 0.4;
          }

          /* Filters */
          .ce-filters {
              display: flex;
              flex-wrap: wrap;
              gap: 1rem;
              justify-content: space-between;
          }
          @media (min-width: 1024px) {
              .ce-filters {
                  gap: 2rem;
              }
          }

          /* Filter Day */
          .ce-day-filters {
              display: flex;
              gap: calc(var(--spacing) * 1);
          }
          @media (max-width: 600px) {
              .ce-day-filters {
                  width: 100%;
              }
              .ec-day-filer {
                  width: 100%;
              }
          }
          .ec-day-filer {
              cursor: pointer;
              font-family: inherit;
              display: flex;
              flex-direction: column;
              color: var(--timetable-button-color);
              background-color: var(--timetable-button-bg);
              border: none;
              padding-block: calc(var(--spacing) * 2);
              padding-inline: calc(var(--spacing) * 2);
          }
          
          .ec-day-filer:first-child {
              border-top-left-radius: 9999px;
              border-bottom-left-radius: 9999px;
              border-top-left-radius: 1.25rem;
              border-bottom-left-radius: 1.25rem;
              padding-left: calc(var(--spacing) * 4);
          }
          .ec-day-filer:last-child {
              border-top-right-radius: 9999px;
              border-bottom-right-radius: 9999px;
              border-top-right-radius: 1.25rem;
              border-bottom-right-radius: 1.25rem;
              padding-right: calc(var(--spacing) * 4);
          }
          @media (min-width: 1024px) {
            .ec-day-filer {
                padding-inline: calc(var(--spacing) * 4);
            }
              .ec-day-filer:first-child {
                  padding-left: calc(var(--spacing) * 6);
              }
              .ec-day-filer:last-child {
                  padding-right: calc(var(--spacing) * 6);
              }
        }

          .ec-day-filer.active {
              color: var(--timetable-button-active-color);
              background: var(--timetable-button-active-bg);
          }
          .ec-day-filer .daymonth {
              font-size: 0.75rem;
              font-weight: 700;
          }
          .ec-day-filer .weekday {
              font-size: 1.25rem;
              font-weight: 900;
              text-transform: uppercase;
          }

          .ce-stage-filter {
              margin-right: auto;
              align-self: stretch;
              z-index: 25;
          }
          .ce-stage-filter .dropdown__trigger{
              min-height: 100%;
          }
          
          
          /* sticky header */
          .ec-header {
              position: sticky;
              top: calc(48px);
              z-index: 20;
              background: var(--time-bg);
          }
          .ec-timeline .ec-time {
              padding-top: 0.25rem;
              padding-bottom: 0.25rem;
          }
          
          /* No Event */
          .ec-list .ec-no-events {
              display: none;
          }
          #timeline-noevents {
              text-align: center;
              padding: 2rem;
              font-size: 2rem;
              color: var(--color-dark);
          }
      `
    ]
}

declare global {
  interface HTMLElementTagNameMap {
    'artist-timeline': ArtistTimeline
  }
}
